import { Controller } from "@hotwired/stimulus"

// "Request AI treatment" modal on a session page, as a multi-stage flow:
//
//   step 1 — type of AI work (skipped when only one type is on offer)
//   step 2 — pick the pictures: the session's Drive folder is preloaded and
//            selectable; manual upload stays available as a fallback
//   step 3 — instructions: one general brief for everything, or one per picture
//   step 4 — confirm what is about to be sent
//
// The Drive folder is fetched lazily the first time the modal opens, so the
// session page itself never waits on the Drive API.
export default class extends Controller {
  static targets = [
    "step", "stepDot", "footerBtn",
    "driveGrid", "driveStatus", "driveEmpty",
    "uploadRows", "uploadTemplate",
    "selectionCount", "pickError",
    "modeGeneral", "modeSpecific",
    "generalPane", "specificPane", "generalText", "specificRows",
    "summaryType", "summaryCount", "summaryMode", "summaryList",
    "hiddenDrive", "hiddenMode", "aiType"
  ]

  static values = {
    driveUrl: String,
    firstStep: { type: Number, default: 0 }
  }

  static PICTURES_STEP = 1

  connect() {
    this.selected = new Map() // drive file id -> {id, name, url, instructions}
    this.driveLoaded = false
    this.showStep(this.firstStepValue)

    // The folder is fetched when the modal opens, not on page load — but food
    // brands skip the type stage and land straight on the picture step, so the
    // "choose pictures" button that would otherwise trigger the fetch is never
    // pressed. Hooking the modal's own show event covers both entry paths.
    this.modalEl = this.element.closest(".modal")
    if (this.modalEl) {
      this.onModalShow = () => {
        if (this.currentStep === this.constructor.PICTURES_STEP) this.loadDrive()
      }
      this.modalEl.addEventListener("show.bs.modal", this.onModalShow)
    }
  }

  disconnect() {
    if (this.modalEl && this.onModalShow) {
      this.modalEl.removeEventListener("show.bs.modal", this.onModalShow)
    }
  }

  // --- stage plumbing ------------------------------------------------------

  showStep(index) {
    this.stepTargets.forEach((pane, i) => { pane.hidden = i !== index })
    this.stepDotTargets.forEach((dot, i) => {
      dot.classList.toggle("is-active", i === index)
      dot.classList.toggle("is-done", i < index)
    })
    this.footerBtnTargets.forEach((btn) => {
      btn.hidden = !String(btn.dataset.step).split(",").includes(String(index))
    })
    this.currentStep = index
  }

  // Highlights the chosen treatment card (the radios themselves are hidden).
  pickType(event) {
    const chosen = event.currentTarget.closest(".ai-type")
    this.element.querySelectorAll(".ai-type").forEach((card) => {
      card.classList.toggle("is-active", card === chosen)
    })
  }

  toPictures(event) {
    if (event) event.preventDefault()
    this.showStep(1)
    this.loadDrive()
  }

  toType(event) {
    if (event) event.preventDefault()
    this.showStep(this.firstStepValue)
  }

  toInstructions(event) {
    if (event) event.preventDefault()
    if (this.totalPictureCount() === 0) {
      this.setPickError("Elija al menos una foto (de la carpeta o subiendo un archivo).")
      return
    }
    this.setPickError("")
    this.renderSpecificRows()
    this.showStep(2)
  }

  backToPictures(event) {
    if (event) event.preventDefault()
    this.showStep(1)
  }

  toConfirm(event) {
    if (event) event.preventDefault()
    this.renderSummary()
    this.showStep(3)
  }

  backToInstructions(event) {
    if (event) event.preventDefault()
    this.showStep(2)
  }

  // --- step 2: Drive folder ------------------------------------------------

  // Fetched once per page load; reopening the modal reuses the grid.
  async loadDrive() {
    if (this.driveLoaded || !this.hasDriveUrlValue) return
    this.driveLoaded = true
    this.setDriveStatus("Cargando las fotos de la sesión…")

    try {
      const resp = await fetch(this.driveUrlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      })
      const data = await resp.json()
      const items = Array.isArray(data.items) ? data.items : []

      if (items.length === 0) {
        this.showDriveEmpty(data.error)
        return
      }
      this.renderDriveGrid(items)
      this.setDriveStatus("")
    } catch (err) {
      this.showDriveEmpty("unavailable")
    }
  }

  renderDriveGrid(items) {
    this.driveGridTarget.innerHTML = items
      .map(
        (item) => `
        <button type="button" class="ai-pick" data-file-id="${this.escapeAttr(item.id)}"
                data-file-name="${this.escapeAttr(item.name)}"
                data-file-url="${this.escapeAttr(item.url)}"
                data-action="ai-request#togglePick">
          <img loading="lazy" src="${this.escapeAttr(item.url)}" alt="${this.escapeAttr(item.name)}">
          <span class="ai-pick__check"><i class="fa-solid fa-check"></i></span>
          <span class="ai-pick__name">${this.escape(item.name)}</span>
        </button>`
      )
      .join("")
    this.driveGridTarget.hidden = false
    this.driveEmptyTarget.hidden = true
  }

  showDriveEmpty(reason) {
    this.driveGridTarget.hidden = true
    this.driveEmptyTarget.hidden = false
    this.setDriveStatus("")
    const message =
      reason === "no_folder"
        ? "Esta sesión todavía no tiene una carpeta de fotos."
        : "No pudimos leer la carpeta de la sesión en este momento."
    this.driveEmptyTarget.querySelector("[data-drive-empty-message]").textContent = message
  }

  togglePick(event) {
    event.preventDefault()
    const button = event.currentTarget
    const id = button.dataset.fileId

    if (this.selected.has(id)) {
      this.selected.delete(id)
      button.classList.remove("is-picked")
    } else {
      this.selected.set(id, {
        id,
        name: button.dataset.fileName,
        url: button.dataset.fileUrl,
        instructions: ""
      })
      button.classList.add("is-picked")
    }
    this.syncSelectionCount()
  }

  // --- step 2: manual upload fallback -------------------------------------

  addUpload(event) {
    if (event) event.preventDefault()
    const index = this.uploadRowsTarget.children.length
    const html = this.uploadTemplateTarget.innerHTML.replace(/__INDEX__/g, index)
    this.uploadRowsTarget.insertAdjacentHTML("beforeend", html)
    this.syncSelectionCount()
  }

  removeUpload(event) {
    event.preventDefault()
    event.currentTarget.closest("[data-upload-row]")?.remove()
    this.syncSelectionCount()
  }

  uploadedFiles() {
    return Array.from(this.uploadRowsTarget.querySelectorAll("input[type=file]"))
      .filter((input) => input.files && input.files.length > 0)
  }

  totalPictureCount() {
    return this.selected.size + this.uploadedFiles().length
  }

  syncSelectionCount() {
    const count = this.totalPictureCount()
    if (this.hasSelectionCountTarget) {
      this.selectionCountTarget.textContent =
        count === 1 ? "1 foto elegida" : `${count} fotos elegidas`
    }
    if (count > 0) this.setPickError("")
  }

  // --- step 3: instructions ------------------------------------------------

  chooseMode() {
    const general = this.modeGeneralTarget.checked
    this.generalPaneTarget.hidden = !general
    this.specificPaneTarget.hidden = general
    this.hiddenModeTarget.value = general ? "general" : "specific"
  }

  // One row per chosen picture, so the customer briefs each one individually.
  renderSpecificRows() {
    const rows = []
    this.selected.forEach((item) => {
      rows.push(`
        <div class="ai-brief" data-brief-id="${this.escapeAttr(item.id)}">
          <img src="${this.escapeAttr(item.url)}" alt="">
          <div class="ai-brief__body">
            <span class="ai-brief__name">${this.escape(item.name)}</span>
            <textarea class="tf-input" rows="2" data-brief-input
                      placeholder="Ej.: mejorar el color, quitar el fondo…">${this.escape(item.instructions)}</textarea>
          </div>
        </div>`)
    })

    this.uploadedFiles().forEach((input) => {
      const name = input.files[0]?.name || "Foto subida"
      rows.push(`
        <div class="ai-brief" data-brief-upload="${this.escapeAttr(input.name)}">
          <div class="ai-brief__placeholder"><i class="fa-duotone fa-image"></i></div>
          <div class="ai-brief__body">
            <span class="ai-brief__name">${this.escape(name)}</span>
            <textarea class="tf-input" rows="2" data-brief-input
                      placeholder="Ej.: mejorar el color, quitar el fondo…"></textarea>
          </div>
        </div>`)
    })

    this.specificRowsTarget.innerHTML = rows.join("")
  }

  // Mirrors the per-picture textareas back onto the model / upload rows.
  collectSpecific() {
    this.specificRowsTarget.querySelectorAll("[data-brief-id]").forEach((row) => {
      const item = this.selected.get(row.dataset.briefId)
      if (item) item.instructions = row.querySelector("[data-brief-input]").value.trim()
    })

    this.specificRowsTarget.querySelectorAll("[data-brief-upload]").forEach((row) => {
      const fileInputName = row.dataset.briefUpload
      const value = row.querySelector("[data-brief-input]").value.trim()
      const target = this.uploadRowsTarget.querySelector(
        `textarea[name="${fileInputName.replace("[picture]", "[instructions]")}"]`
      )
      if (target) target.value = value
    })
  }

  // --- step 4: confirm -----------------------------------------------------

  renderSummary() {
    this.collectSpecific()

    const general = this.modeGeneralTarget.checked
    const checkedType = this.aiTypeTargets.find((r) => r.checked)
    this.summaryTypeTarget.textContent =
      checkedType?.value === "ai_staging" ? "AI Staging" : "AI Enhancing"
    this.summaryCountTarget.textContent = this.totalPictureCount()
    this.summaryModeTarget.textContent = general
      ? "Una instrucción para todas las fotos"
      : "Instrucciones por foto"

    const rows = []
    this.selected.forEach((item) => {
      rows.push(`<li><span class="ai-summary__name">${this.escape(item.name)}</span>
        <span class="ai-summary__note">${this.escape(general ? this.generalTextTarget.value : item.instructions) || "—"}</span></li>`)
    })
    this.uploadedFiles().forEach((input) => {
      rows.push(`<li><span class="ai-summary__name">${this.escape(input.files[0]?.name || "Foto subida")}</span>
        <span class="ai-summary__note">${general ? this.escape(this.generalTextTarget.value) || "—" : "—"}</span></li>`)
    })
    this.summaryListTarget.innerHTML = rows.join("")

    this.writeHiddenDrive()
  }

  // Selected Drive pictures travel as ordinary form params.
  writeHiddenDrive() {
    const general = this.modeGeneralTarget.checked
    let i = 0
    const inputs = []
    this.selected.forEach((item) => {
      const note = general ? this.generalTextTarget.value : item.instructions
      inputs.push(`
        <input type="hidden" name="drive_pictures[${i}][id]" value="${this.escapeAttr(item.id)}">
        <input type="hidden" name="drive_pictures[${i}][name]" value="${this.escapeAttr(item.name)}">
        <input type="hidden" name="drive_pictures[${i}][instructions]" value="${this.escapeAttr(note)}">`)
      i += 1
    })
    this.hiddenDriveTarget.innerHTML = inputs.join("")
  }

  // --- helpers -------------------------------------------------------------

  setDriveStatus(message) {
    if (!this.hasDriveStatusTarget) return
    this.driveStatusTarget.textContent = message
    this.driveStatusTarget.hidden = message.length === 0
  }

  setPickError(message) {
    if (!this.hasPickErrorTarget) return
    this.pickErrorTarget.textContent = message
    this.pickErrorTarget.hidden = message.length === 0
  }

  escape(value) {
    const div = document.createElement("div")
    div.textContent = value == null ? "" : String(value)
    return div.innerHTML
  }

  escapeAttr(value) {
    return this.escape(value).replace(/"/g, "&quot;")
  }
}
